import { Stack } from 'braid-design-system';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import SkeletonLine from 'src/components/SkeletonLine/SkeletonLine';
import useAuthenticated from 'src/hooks/useAuthenticated';

import { CareerFeedAIAssistantContent } from './CareerFeedAIAssistantContent/CareerFeedAIAssistantContent';
import { CareerFeedAIAssistantWidgetContainer } from './CareerFeedAIAssistantWidgetContainer/CareerFeedAIAssistantWidgetContainer';
import { useCareerFeedAIAssistant } from './hooks/useCareerFeedAIAssistant';
import { useRecommendationForCareerFeedAiAssistant } from './hooks/useRecommendationForCareerFeedAiAssistant';

/** *
 * This component is responsible for displaying the AI Assistant for the Career Feed.
 * This is still in in discovery phase and the implementation of this component might change drastically.
 * The reason why this is in the code base is so that people can start playing around with it
 * and get feedback on the implementation.
 */
export const CareerFeedAIAssistant = () => {
  const {
    error: isRecommendationError,
    loading: isRecommendationLoading,
    isRefreshLoading,
    jobIds,
  } = useRecommendationForCareerFeedAiAssistant();

  const authenticateState = useAuthenticated();

  const {
    sequence,
    persistedCandidateChatContext,
    progress,
    tracking: { trackGenAIChatOutputDisplayed, trackGenAIChatStarted },
  } = useCareerFeedAIAssistant();

  const [isInView, setIsInView] = useState(false);

  const { ref: trackedComponent } = useInView({
    onChange(inView) {
      setIsInView(inView);
    },
    delay: 500,
    threshold: 0.5,
  });

  const readyToFireTracking = useMemo(
    () => isInView && authenticateState === 'authenticated',
    [authenticateState, isInView],
  );

  const [isGenAIChatChangeEventFired, setIsGenAIChatChangeEventFired] =
    useState(false);

  const [chatUpdated, setChatUpdated] = useState(false);
  useEffect(() => {
    if (
      progress === 'gatheringRefinement' ||
      progress === 'collectGeneralRequirementsAtLast' ||
      progress === 'recsRefined' ||
      sequence
    ) {
      setChatUpdated(true);
      return () => {
        // when the dependency changes, we release the `isGenAIChatChangeEventFired` lock, so that the next time the component is rendered, it can fire the event again
        setIsGenAIChatChangeEventFired(false);
      };
    }
    setChatUpdated(false);
  }, [chatUpdated, progress, sequence]);

  useEffect(() => {
    if (readyToFireTracking && chatUpdated && !isGenAIChatChangeEventFired) {
      if (sequence === 1) {
        trackGenAIChatStarted();
      }
      trackGenAIChatOutputDisplayed();

      setIsGenAIChatChangeEventFired(true);
    }
  }, [
    chatUpdated,
    isGenAIChatChangeEventFired,
    readyToFireTracking,
    sequence,
    trackGenAIChatOutputDisplayed,
    trackGenAIChatStarted,
  ]);

  const jobsCount = jobIds.length;
  const isLoadingState =
    (isRecommendationLoading && !isRefreshLoading) ||
    (progress === 'initial' && isRefreshLoading);

  if (isRecommendationError) {
    return null;
  }

  if (!persistedCandidateChatContext && !isLoadingState && jobsCount === 0) {
    return null;
  }

  return (
    <CareerFeedAIAssistantWidgetContainer
      showHeading={!isLoadingState}
      ref={trackedComponent}
    >
      {isLoadingState ? (
        <Stack space="medium">
          <SkeletonLine type="text" width={200} size="small" />
          <SkeletonLine type="text" width={260} size="xsmall" />
        </Stack>
      ) : (
        <CareerFeedAIAssistantContent />
      )}
    </CareerFeedAIAssistantWidgetContainer>
  );
};
